import type { SelectionDecision, WatchPart } from '../api/client'
import { CaseVariantPicker } from './CaseVariantPicker'
import { compactSpecs, formatLabel, formatPrice } from '../lib/partDisplay'
import { providerDisplayName } from '../outbound/links'

type PartCardProps = {
  partType: string
  part: WatchPart
  decision?: SelectionDecision
  isUpdated?: boolean
  purchaseHref?: string | null
  onPurchaseClick?: () => void
  onCaseVariantChange?: (variantId: string) => void
}

export function PartCard({
  partType,
  part,
  decision,
  isUpdated,
  purchaseHref,
  onPurchaseClick,
  onCaseVariantChange,
}: PartCardProps) {
  const specs = compactSpecs(part)
  const providerLabel = providerDisplayName(part.provider_id)

  return (
    <article className={`part-card${isUpdated ? ' part-card--updated' : ''}${partType === 'strap' ? ' part-card--full-width' : ''}`}>
      <div className="part-image-box">
        {part.image_url ? (
          <img src={part.image_url} alt={part.name} />
        ) : (
          <div className="part-image-placeholder">
            <span>{partType.slice(0, 2).toUpperCase()}</span>
          </div>
        )}
      </div>

      <div className="part-content">
        <p className="part-type">{formatLabel(partType)}</p>
        <h3 className="part-name">{part.name}</h3>

        {specs.length > 0 ? (
          <dl className="part-specs">
            {specs.map(([label, value]) => (
              <div key={label} className="part-spec-row">
                <dt className="spec-key">{label}</dt>
                <dd className="spec-val">{value}</dd>
              </div>
            ))}
          </dl>
        ) : null}

        {decision ? (
          <p className="selection-reason" title={decision.reason}>
            {decision.reason}
          </p>
        ) : null}

        {partType === 'case' ? (
          <CaseVariantPicker part={part} onVariantChange={onCaseVariantChange} />
        ) : null}

        <div className="part-footer">
          <span className="part-provider">{providerLabel}</span>
          <span className="part-price">{formatPrice(part.price, part.currency)}</span>
        </div>

        {purchaseHref ? (
          <a
            className="part-buy-link"
            href={purchaseHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onPurchaseClick?.()}
          >
            Buy at {providerLabel}
          </a>
        ) : null}
      </div>
    </article>
  )
}
